// ─────────────────────────────────────────────────────────────────────────────
// Target — the interactive SVG target faces.
//
// Structure (#2063): ONE `<FaceCanvas>` draws every face. What differs per
// face is data, not code, and lives in `target/FaceLayers.jsx` as the
// `face -> layers` map. The 17 named face exports below are thin bindings kept
// only so existing call sites (and `TargetFace`) keep working unchanged.
//
//   target/FaceLayers.jsx      ring geometry + the face → layers map
//   target/DraggableArrows.jsx the arrow overlay (FIXED glyph styles)
//   hooks/useTargetInteraction shared tap / place / drag behaviour
//
// ⚠ The arrow overlay never takes a score-derived colour. FaceCanvas hands
// DraggableArrows nothing but geometry and the face id — see the carve-out
// documented in target/DraggableArrows.jsx and round-detail/svgDots.js.
// ─────────────────────────────────────────────────────────────────────────────
import { useTargetInteraction } from "../hooks/useTargetInteraction";
import { faceLayers } from "./target/FaceLayers";
import { DraggableArrows } from "./target/DraggableArrows";

// Re-exported so `solveCalloutPositions` keeps its historical import path.
export { solveCalloutPositions } from "./target/DraggableArrows";

// ─────────────────────────────────────────────────────────────────────────────
// FaceCanvas — the single target canvas. Draws, in order: the square paper
// mat (faces that have one), decorative art, the scoring rings, the zone-value
// labels, then the arrow overlay on top. Every layer but the overlay comes
// from the face's FACE_LAYERS entry.
// ─────────────────────────────────────────────────────────────────────────────
function FaceCanvas({
  face,
  arrows = [],
  onTap,
  onArrowMove,
  size = 440,
  interactive = false,
  showArrowNums = true,
}) {
  const layers = faceLayers(face);
  const t = useTargetInteraction(face, { onTap, onArrowMove, interactive, arrowCount: arrows.length });

  return (
    <svg
      ref={t.ref}
      className="target-svg"
      viewBox="0 0 240 240"
      style={{
        maxWidth: size,
        cursor: interactive ? "crosshair" : "default",
        ...(layers.background ? { background: layers.background } : null),
      }}
      onClick={t.handleClick}
      onPointerDown={t.handleSVGPointerDown}
      onPointerMove={t.handlePointerMove}
      onPointerUp={t.handlePointerUp}
      onPointerCancel={t.handlePointerUp}
    >
      {/* square paper/mat — the real-world target face sits on square paper */}
      {layers.mat && <rect x="0" y="0" width="240" height="240" fill="var(--surface-2)" />}
      {layers.decor?.()}
      {layers.rings()}
      {layers.labels?.()}
      <DraggableArrows
        arrows={arrows}
        showArrowNums={showArrowNums}
        draggable={interactive && !!onArrowMove}
        liftedIndex={t.dragIndex}
        face={t.face}
        onStartDrag={t.startArrowDrag}
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The named face components. Each is a binding of FaceCanvas to one face id;
// the face id is pinned AFTER the spread because these components have always
// ignored a caller-supplied `face` (the two that genuinely take one destructure
// it explicitly). Behaviour, props and defaults are unchanged.
// ─────────────────────────────────────────────────────────────────────────────

/** WA 10-ring face (40 cm indoor / 122 cm outdoor). */
export function WATarget(props) {
  return <FaceCanvas {...props} face="wa40" />;
}

/** WA Vegas 3-spot inner face — three spots stacked vertically. */
export function WATargetTripleSpot(props) {
  return <FaceCanvas {...props} face="vegas" />;
}

/** WA triangular triple-spot inner face (#165) — two spots low, one high. */
export function WATarget3SpotTri(props) {
  return <FaceCanvas {...props} face="3spot-tri" />;
}

/** NFAA indoor 5-spot quincunx face (#66) — 5 spots in an X, each X/5/4/3. */
export function NFAATarget5Spot(props) {
  return <FaceCanvas {...props} face="nfaa5spot" />;
}

/** NFAA indoor single-spot (40 cm face). */
export function NFAATargetSingleSpot(props) {
  return <FaceCanvas {...props} face="nfaaspot" />;
}

/** NFAA Field face (single-spot, all four sizes proportional). */
export function NFAAFieldTarget(props) {
  return <FaceCanvas {...props} face="nfaafield" />;
}

/** 3D animal face (#67, #157) — vitals scored 12 / 10 / 8 / 5. */
export function AnimalTarget3D(props) {
  return <FaceCanvas {...props} face="3d" />;
}

/** NFAA Championship Hunter face (all four sizes proportional). */
export function NFAAHunterTarget(props) {
  return <FaceCanvas {...props} face="nfaahunter" />;
}

/** Worcester — monochrome indoor face, white centre 5 down to 1. No X ring. */
export function WorcesterTarget(props) {
  return <FaceCanvas {...props} face="worcester" />;
}

/** 5-zone colour-band face for NFAA 810 and UK imperial rounds (9/7/5/3/1). */
export function ImperialTarget5Zone(props) {
  return <FaceCanvas {...props} face="imperial5zone" />;
}

/** WA-geometry 5-zone face (wa5zone) — same bands and scoring as Imperial. */
export function WA5ZoneTarget(props) {
  return <FaceCanvas {...props} face="wa5zone" />;
}

/** WA 6-ring 80 cm face (wa6ring, "WP-80 6 ring", compound 50 m). */
export function WATarget6Ring(props) {
  return <FaceCanvas {...props} face="wa6ring" />;
}

/** WA 5-ring face (wa5ring80 / wa5ring122), scored X/10/9/8/7/6. */
export function WATarget5Ring({ face = "wa5ring80", ...rest }) {
  return <FaceCanvas {...rest} face={face} />;
}

/** WA Field single-spot face (wafield40/60/80), scored X/5/4/3/2/1. */
export function WAFieldTarget({ face = "wafield80", ...rest }) {
  return <FaceCanvas {...rest} face={face} />;
}

/** WA Field 3-spot face — 3 × 20 cm spots, same 6-zone scoring per spot. */
export function WAFieldTarget3Spot(props) {
  return <FaceCanvas {...props} face="wafield3spot" />;
}

/** NFAA 3D 2-arrow face: three rings (11/10/8), no body zone — outside is M. */
export function AnimalTarget3D2Arrow(props) {
  return <FaceCanvas {...props} face="3d-2arrow" />;
}

/** IBO 3D face: as AnimalTarget3D but the inner vital scores 11, not 12. */
export function AnimalTargetIBO3D(props) {
  return <FaceCanvas {...props} face="ibo3d" />;
}

// ─────────────────────────────────────────────────────────────────────────────
// TargetFace — picks the right face component from a face-id string
// ─────────────────────────────────────────────────────────────────────────────
export function TargetFace({ face = "wa40", ...rest }) {
  switch (face) {
    case "vegas":     return <WATargetTripleSpot {...rest} />;
    case "3spot-tri": return <WATarget3SpotTri   {...rest} />;
    case "nfaa5spot": return <NFAATarget5Spot        {...rest} />;
    case "nfaaspot":  return <NFAATargetSingleSpot  {...rest} />;
    case "nfaafield": return <NFAAFieldTarget        {...rest} />;
    case "3d":        return <AnimalTarget3D     {...rest} />;
    case "ibo3d":     return <AnimalTargetIBO3D   {...rest} />;
    case "3d-2arrow":    return <AnimalTarget3D2Arrow  {...rest} />;
    case "imperial5zone": return <ImperialTarget5Zone  {...rest} />;
    case "wa5zone":       return <WA5ZoneTarget         {...rest} />;
    case "wa6ring":       return <WATarget6Ring         {...rest} />;
    case "wa5ring80":
    case "wa5ring122":    return <WATarget5Ring  face={face} {...rest} />;
    case "wafield40":
    case "wafield60":
    case "wafield80":     return <WAFieldTarget     face={face} {...rest} />;
    case "wafield3spot":  return <WAFieldTarget3Spot             {...rest} />;
    case "worcester":     return <WorcesterTarget       {...rest} />;
    case "nfaahunter":    return <NFAAHunterTarget      {...rest} />;
    default:              return <WATarget              {...rest} />;
  }
}
